import {
  Box,
  CardContent,
  CircularProgress,
  Card as MuiCard,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import HorizontalScrollContainer from "common/components/HorizontalScrollContainer";
import {
  useYieldByTypePerYear,
  YieldByTypeYearData,
} from "features/insights/hooks/useInsightsDashboardQueries";
import { useCurrentFarm } from "model/useFarmQueries";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const YieldCard = styled(MuiCard)({
  minWidth: 420,
  maxWidth: 520,
  backgroundColor: "white",
  borderRadius: 16,
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
  },
});

const YEAR_COLORS = [COLORS.dahlia[500], COLORS.dahlia[300]];

type ChartRow = {
  type: string;
  unit: string;
  [year: string]: string | number;
};

export function darkenColor(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * factor)}, ${Math.round(
    g * factor
  )}, ${Math.round(b * factor)})`;
}

const LIFT = 3;
const TRANSITION = "transform 0.15s ease, fill 0.15s ease";

function HoverBarShape(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  index?: number;
  activeIndex: number | null;
}) {
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill = "",
    index,
    activeIndex,
  } = props;
  const isActive = activeIndex !== null && index === activeIndex;

  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={isActive ? darkenColor(fill, 0.85) : fill}
      rx={4}
      ry={4}
      style={{
        transition: TRANSITION,
        transform: isActive ? `translateY(-${LIFT}px)` : "translateY(0)",
      }}
    />
  );
}

export function formatAmount(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

type YieldCategory = "harvest" | "milk" | "meat";

function classifyRow(row: YieldByTypeYearData): YieldCategory {
  const type = row["YieldSummary.yieldTypeDisplay"];
  if (type === "Milk") return "milk";
  if (type === "Meat") return "meat";
  return "harvest";
}

export function transformData(
  rows: YieldByTypeYearData[],
  category: YieldCategory
): {
  chartData: ChartRow[];
  years: string[];
} {
  const filtered = rows.filter((row) => classifyRow(row) === category);
  if (filtered.length === 0) return { chartData: [], years: [] };

  const yearsSet = new Set<string>();
  const byType = new Map<string, ChartRow>();

  for (const row of filtered) {
    const type = row["YieldSummary.yieldTypeDisplay"];
    const rawAmount = Number(row["YieldSummary.totalAmount"]);
    const rawUnit = row["YieldSummary.amountUnitDisplay"];
    const yearRaw = row["YieldSummary.eventDate.year"];
    const year = new Date(yearRaw).getFullYear().toString();

    let amount: number;
    let unit: string;

    if (category === "harvest") {
      amount = rawUnit === "kg" ? rawAmount / 1000 : rawAmount;
      unit = "t";
    } else if (category === "milk") {
      amount = rawAmount;
      unit = "litres";
    } else {
      amount = rawUnit === "t" ? rawAmount * 1000 : rawAmount;
      unit = "kg";
    }

    yearsSet.add(year);

    if (!byType.has(type)) {
      byType.set(type, { type, unit });
    }
    byType.get(type)![year] = amount;
  }

  const years = Array.from(yearsSet)
    .sort((a, b) => Number(b) - Number(a))
    .slice(0, 2);

  const chartData = Array.from(byType.values());
  if (years.length > 0) {
    chartData.sort(
      (a, b) => (Number(b[years[0]]) || 0) - (Number(a[years[0]]) || 0)
    );
  }

  return { chartData, years };
}

type TooltipEntry = {
  name: string;
  value: number;
  color: string;
  payload: ChartRow;
};

function CustomTooltip({
  active,
  payload,
  label,
  pinnedLabel,
  pinnedPayload,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  pinnedLabel?: string;
  pinnedPayload?: TooltipEntry[];
}) {
  const showLabel = active && payload?.length ? label : pinnedLabel;
  const showPayload = active && payload?.length ? payload : pinnedPayload;

  if (!showPayload?.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: `${BORDER_RADIUS.small}px`,
        p: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {showLabel}
      </Typography>
      {showPayload.map((entry) => (
        <Box
          key={entry.name}
          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "2px",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: "#333" }}>
            {entry.name}:{" "}
            {entry.value.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })}{" "}
            {entry.payload.unit}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function YieldBarChart({
  title,
  unitLabel,
  chartData,
  years,
}: {
  title: string;
  unitLabel: string;
  chartData: ChartRow[];
  years: string[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const pinned = useRef(false);

  const handleMouseMove = useCallback(
    (state: { activeTooltipIndex?: number }) => {
      if (pinned.current) return;
      if (state?.activeTooltipIndex !== undefined) {
        setActiveIndex(state.activeTooltipIndex);
      }
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    if (pinned.current) return;
    setActiveIndex(null);
  }, []);

  const handleClick = useCallback(
    (state: { activeTooltipIndex?: number }) => {
      const idx = state?.activeTooltipIndex;
      if (idx !== undefined) {
        if (pinned.current && activeIndex === idx) {
          pinned.current = false;
          setActiveIndex(null);
        } else {
          pinned.current = true;
          setActiveIndex(idx);
        }
      }
    },
    [activeIndex]
  );

  return (
    <YieldCard>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unitLabel}
            </Typography>
          </Box>
          {years.length > 1 && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {years.map((year, i) => (
                <Box
                  key={year}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      backgroundColor: YEAR_COLORS[i],
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {year}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ width: "100%", height: 200 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f0f0f0"
              />
              <XAxis
                dataKey="type"
                tick={{ fontSize: 12, fill: "#71717a" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#71717a" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatAmount}
                width={50}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    pinnedLabel={
                      pinned.current && activeIndex !== null
                        ? chartData[activeIndex]?.type
                        : undefined
                    }
                    pinnedPayload={
                      pinned.current && activeIndex !== null
                        ? years.map((year, i) => ({
                            name: year,
                            value: Number(chartData[activeIndex]?.[year]) || 0,
                            color: YEAR_COLORS[i],
                            payload: chartData[activeIndex],
                          }))
                        : undefined
                    }
                  />
                }
                cursor={false}
                wrapperStyle={
                  pinned.current && activeIndex !== null
                    ? { visibility: "visible" as const }
                    : undefined
                }
              />
              {years.map((year, i) => (
                <Bar
                  key={year}
                  dataKey={year}
                  name={year}
                  fill={YEAR_COLORS[i]}
                  maxBarSize={48}
                  shape={(props: Record<string, unknown>) => (
                    <HoverBarShape {...props} activeIndex={activeIndex} />
                  )}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </YieldCard>
  );
}

const CATEGORIES: {
  category: YieldCategory;
  title: string;
  unitLabel: string;
}[] = [
  { category: "harvest", title: "Harvest Yield", unitLabel: "tonnes" },
  { category: "milk", title: "Milk Production", unitLabel: "litres" },
  { category: "meat", title: "Meat Production", unitLabel: "kg" },
];

export default function YieldInsightsCards() {
  const { data: currentFarm } = useCurrentFarm();
  const { data: result, isLoading } = useYieldByTypePerYear(currentFarm?.guid);

  const categoryData = useMemo(() => {
    const rows = result?.data ?? [];
    return CATEGORIES.map(({ category, title, unitLabel }) => ({
      title,
      unitLabel,
      ...transformData(rows, category),
    })).filter(({ chartData }) => chartData.length > 0);
  }, [result?.data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (categoryData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No yield data
        </Typography>
      </Box>
    );
  }

  return (
    <HorizontalScrollContainer>
      {categoryData.map(({ title, unitLabel, chartData, years }) => (
        <YieldBarChart
          key={title}
          title={title}
          unitLabel={unitLabel}
          chartData={chartData}
          years={years}
        />
      ))}
    </HorizontalScrollContainer>
  );
}
